import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStations, useOrders } from "../hooks/useOrders";
import { useKitchenRealtime } from "../hooks/useKitchenRealtime";
import { useKitchenStore } from "../stores/kitchen.store";
import StationBoard from "../components/StationBoard";
import KdsScaleButton from "../components/KdsScaleButton";
import { useKdsScale } from "../hooks/useKdsScale";
import { boardColumns } from "../lib/station-layout";
import { useViewportWidth } from "../hooks/useViewportWidth";
import TableBoard from "./TableBoard";
import HistoryView from "./HistoryView";
import { Volume2, VolumeX, LogOut, RotateCw, Lock, LockOpen } from "lucide-react";
import { clientConfigApi, foodBlockApi } from "../api/client";
import { useTranslation } from "../i18n";

export default function ChefOverview() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const clientId = useKitchenStore((s) => s.clientId);
  const jwtToken = useKitchenStore((s) => s.jwtToken);
  const userName = useKitchenStore((s) => s.userName);
  const audioEnabled = useKitchenStore((s) => s.audioEnabled);
  const toggleAudio = useKitchenStore((s) => s.toggleAudio);
  const logout = useKitchenStore((s) => s.logout);
  const stationOrder = useKitchenStore((s) => s.stationOrder);
  const setStationOrder = useKitchenStore((s) => s.setStationOrder);
  const moveStation = useKitchenStore((s) => s.moveStation);
  const viewMode = useKitchenStore((s) => s.viewMode);
  const setViewMode = useKitchenStore((s) => s.setViewMode);
  // Feature 113 — blocco ordini cibo
  const foodBlock = useKitchenStore((s) => s.foodBlock);
  const setFoodBlock = useKitchenStore((s) => s.setFoodBlock);
  const [foodBlockSaving, setFoodBlockSaving] = useState(false);
  // Feature 148 (US2) — toggle Live/Storico. Stato locale, default Live.
  // History mode mounts HistoryView on its own query, in place of the live
  // board — which stays exactly as it was and comes straight back.
  const [historyOpen, setHistoryOpen] = useState(false);

  // Connect real-time WebSocket → React Query bridge
  useKitchenRealtime(jwtToken);

  // Feature 113 — stato iniziale del blocco dal client-config (on mount).
  // Gli aggiornamenti successivi arrivano via WS (food_block_changed).
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    clientConfigApi
      .get(clientId)
      .then((cfg) => {
        if (cancelled) return;
        setFoodBlock({
          enabled: !!cfg.foodOrderBlockEnabled,
          active: !!cfg.foodOrderBlockActive,
        });
      })
      .catch(() => {
        /* fallback: lascia lo stato di default (toggle nascosto) */
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, setFoodBlock]);

  const handleToggleFoodBlock = async () => {
    if (!clientId || foodBlockSaving) return;
    const next = !foodBlock.active;
    setFoodBlockSaving(true);
    // Optimistic: move now, the websocket confirms.
    setFoodBlock({ enabled: foodBlock.enabled, active: next });
    try {
      await foodBlockApi.toggle(clientId, next);
    } catch {
      // Rollback in caso di errore.
      setFoodBlock({ enabled: foodBlock.enabled, active: !next });
    } finally {
      setFoodBlockSaving(false);
    }
  };

  const { data: stations } = useStations(clientId);
  const { data: orders } = useOrders(clientId);

  // Init stationOrder when stations load (only if not already set)
  useEffect(() => {
    if (stations && stations.length > 0) {
      const stationIds = stations.map((s: any) => s.id);
      const hasAll = stationOrder.length === stationIds.length &&
        stationOrder.every((id: string) => stationIds.includes(id));
      if (!hasAll) {
        // Preserve existing order for known stations, append new ones
        const known = stationOrder.filter((id: string) => stationIds.includes(id));
        const newIds = stationIds.filter((id: string) => !known.includes(id));
        setStationOrder([...known, ...newIds]);
      }
    }
  }, [stations]);

  // Sort stations according to saved order
  const sortedStations = useMemo(() => {
    if (!stations || stations.length === 0) return [];
    if (stationOrder.length === 0) return stations;
    return [...stations].sort((a: any, b: any) => {
      const ai = stationOrder.indexOf(a.id);
      const bi = stationOrder.indexOf(b.id);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  }, [stations, stationOrder]);

  // Feature 257 — the arrangement. Read the width once here, not per station.
  const viewportWidth = useViewportWidth();
  // The size of the characters on THIS monitor. Remembered here, sent nowhere:
  // the screen by the pass and the one at the bar are read from different
  // distances. The columns follow it, because bigger type needs a wider column.
  const { scale: kdsScale, cycle: cycleKdsScale } = useKdsScale();
  const columns = useMemo(
    () =>
      boardColumns(
        sortedStations,
        (orders ?? []).filter(
          (o: any) => o.status !== "COMPLETED" && o.status !== "CANCELLED",
        ),
      ),
    [sortedStations, orders],
  );

  // If not authenticated, redirect to login
  if (!jwtToken) {
    navigate("/login");
    return null;
  }

  if (!clientId) {
    navigate("/login");
    return null;
  }

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    // The page must be EXACTLY the viewport (h-screen) with no document
    // scroll: the kitchen screen does not scroll by touch, so anything that
    // scrolls the document is out of reach. Each StationColumn scrolls itself
    // instead. Before this, min-h-screen plus an auto grid let the page grow
    // with the tallest column and pushed dishes off the bottom.
    <div className="h-screen flex flex-col bg-kds-bg p-4 overflow-hidden">
      {/* Header */}
      {/* Feature 257 — the toolbar is furniture: on a 15-inch till it took
          97 px of 768 before a dish was drawn. The page title drops from the
          order-number size to a label, and the gap below halves. Every control
          stays where it was: one a cook cannot find is worse than one that
          costs a few pixels. */}
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <h1 className="text-kds-station text-kds-text">{t("kds.chef.title", "Chef Overview")}</h1>
        <div className="flex items-center gap-4">
          <span className="text-kds-muted text-kds-meta">
            {t("kds.chef.activeOrders", "{{count}} ordini attivi", { count: orders?.length ?? 0 })}
          </span>

          {/* The size of the characters on THIS monitor. Remembered here, sent
              nowhere: the screen by the pass and the one at the bar are read
              from different distances. */}
          <KdsScaleButton scale={kdsScale} onCycle={cycleKdsScale} />

          {/* Feature 148 — Segmented toggle: Live / Storico */}
          <div className="flex rounded-lg bg-kds-surface overflow-hidden border border-kds-muted/20">
            <button
              onClick={() => setHistoryOpen(false)}
              className={[
                "px-4 py-2 text-kds-action min-h-touch transition-colors",
                !historyOpen
                  ? "bg-kds-card text-kds-text"
                  : "text-kds-muted hover:text-kds-text",
              ].join(" ")}
              title={t("kds.chef.liveTitle", "Vista operativa live (default)")}
            >
              {t("kds.chef.live", "Live")}
            </button>
            <button
              onClick={() => setHistoryOpen(true)}
              className={[
                "px-4 py-2 text-kds-action min-h-touch transition-colors",
                historyOpen
                  ? "bg-kds-card text-kds-text"
                  : "text-kds-muted hover:text-kds-text",
              ].join(" ")}
              title={t("kds.chef.historyTitle", "Storico ondate servite/completate della giornata")}
            >
              {t("kds.chef.history", "Storico")}
            </button>
          </div>

          {/* Segmented toggle: Per postazione / Per tavolo (solo in Live) */}
          {!historyOpen && (
          <div className="flex rounded-lg bg-kds-surface overflow-hidden border border-kds-muted/20">
            <button
              onClick={() => setViewMode("station")}
              className={[
                "px-4 py-2 text-kds-action min-h-touch transition-colors",
                viewMode === "station"
                  ? "bg-kds-card text-kds-text"
                  : "text-kds-muted hover:text-kds-text",
              ].join(" ")}
              title={t("kds.chef.byStationTitle", "Vista per postazione (default)")}
            >
              {t("kds.chef.byStation", "Per postazione")}
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={[
                "px-4 py-2 text-kds-action min-h-touch transition-colors",
                viewMode === "table"
                  ? "bg-kds-card text-kds-text"
                  : "text-kds-muted hover:text-kds-text",
              ].join(" ")}
              title={t("kds.chef.byTableTitle", "Vista per tavolo")}
            >
              {t("kds.chef.byTable", "Per tavolo")}
            </button>
          </div>
          )}

          {/* Feature 113 — Toggle blocco ordini cibo (visibile solo se la
              funzionalità è disponibile per il locale). */}
          {foodBlock.enabled && (
            <button
              onClick={handleToggleFoodBlock}
              disabled={foodBlockSaving}
              className={[
                "flex items-center gap-1.5 px-3 py-2 rounded-lg font-semibold min-h-touch transition-colors disabled:opacity-60",
                foodBlock.active
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : "bg-kds-card text-kds-text hover:bg-kds-card/70",
              ].join(" ")}
              title={
                foodBlock.active
                  ? t("kds.chef.foodBlockActiveTitle", "Blocco ordini cibo ATTIVO — clicca per sbloccare")
                  : t("kds.chef.foodBlockTitle", "Blocca gli ordini cibo dei camerieri")
              }
            >
              {foodBlock.active ? <Lock size={18} /> : <LockOpen size={18} />}
              {foodBlock.active ? t("kds.chef.foodBlocked", "Cibo bloccato") : t("kds.chef.foodBlock", "Blocco cibo")}
            </button>
          )}

          <button
            onClick={() => window.location.reload()}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-kds-card text-kds-text font-semibold min-h-touch hover:bg-kds-card/70 transition-colors"
            title={t("kds.chef.reloadTitle", "Aggiorna la schermata")}
          >
            <RotateCw size={18} />
            {t("kds.common.refresh", "Aggiorna")}
          </button>

          <button
            onClick={toggleAudio}
            className="p-2 rounded-lg bg-kds-surface min-h-touch min-w-touch flex items-center justify-center"
            title={audioEnabled ? t("kds.chef.audioOff", "Disattiva audio") : t("kds.chef.audioOn", "Attiva audio")}
          >
            {audioEnabled ? <Volume2 size={18} /> : <VolumeX size={18} className="text-kds-muted" />}
          </button>
          <div className="w-3 h-3 rounded-full bg-kds-ready animate-pulse" />
          {userName && (
            <span className="text-kds-muted text-kds-meta">{userName}</span>
          )}
          <button
            onClick={handleLogout}
            className="p-2 rounded-lg bg-kds-surface hover:bg-red-500/20 min-h-touch min-w-touch flex items-center justify-center transition-colors"
            title={t("kds.common.logout", "Esci")}
          >
            <LogOut size={18} className="text-kds-muted" />
          </button>
        </div>
      </div>

      {/* Main content — Feature 148: Storico (US2) oppure vista live.
          In Storico si monta HistoryView (query isolata, snapshot); la vista
          live per postazione/tavolo resta invariata e ripristinabile. */}
      {historyOpen ? (
        <HistoryView clientId={clientId} />
      ) : viewMode === "table" ? (
        // Per-table view: renders TableBoard which groups by tableId
        // useKitchenRealtime stays mounted above so realtime works for both views.
        <TableBoard clientId={clientId} />
      ) : (
        <StationBoard
          columns={columns}
          viewportWidth={viewportWidth}
          scale={kdsScale}
          stations={sortedStations}
          onMoveStation={moveStation}
        />
      )}
    </div>
  );
}
